/** Copyright © 2025 Qcells. All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized use,
 * duplication, or distribution of software is strictly prohibited.
 */
import type { AccountInfo, Configuration, RedirectRequest } from '@azure/msal-browser';
import type { CookieAttributes } from 'js-cookie';
import Cookies from 'js-cookie';

import { APP_MODE, FLEET_EMBED_PARAM, FLEET_EMBED_PARAM_VALUE, PROVIDER_URL } from '@hems/util/src/constant/constant';
import type { TenantType } from '@hems/util/src/constant/msal';
import { TENANT } from '@hems/util/src/constant/msal';
import { AUTH_TYPE_CODE_BY_FLEET_ROLE, ROLE_NAME } from '@hems/util/src/constant/role';
import type { RoleNameValue, RoleCodeValue, FleetRoleValue } from '@hems/util/src/constant/role';
import { Helper } from '@hems/util/src/index';

import type { Role } from 'hems';

import type { AuthToken } from 'hems/auth';

const RoleMap: Readonly<Record<RoleCodeValue, Role>> = {
  10: 'user',
  20: 'installer',
  60: 'dev',
  70: 'service',
  90: 'admin',
};

export const getRoleName = (roleCode: RoleCodeValue) => RoleMap[roleCode];

/** Fleet Role은 Installer Role과 동일하게 처리 */
export const isInstallerRole = (roleName: RoleNameValue) => roleName === ROLE_NAME.INSTALLER;

export const parseJwt = (jwt?: string): Record<string, string> | null => {
  if (!jwt) {
    return null;
  }
  try {
    const base64Url = jwt.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

const TOKEN_KEY = {
  ACCESS_TOKEN: 'access_token',
  ID_TOKEN: 'id_token',
} as const;

const cookieOptions: CookieAttributes = {
  path: '/',
  sameSite: 'strict',
};

export const saveAuthToken = (authToken: AuthToken) => {
  Cookies.set(TOKEN_KEY.ACCESS_TOKEN, encodeURIComponent(authToken.accessToken || ''), cookieOptions);
  Cookies.set(TOKEN_KEY.ID_TOKEN, encodeURIComponent(authToken.idToken || ''), cookieOptions);
};

export const saveAccessTokenForIframe = (token: string): Promise<void> => {
  const iframeCookieOptions: CookieAttributes = {
    path: '/',
    sameSite: 'none',
    secure: true,
  };

  return new Promise((resolve, reject) => {
    try {
      Cookies.set(TOKEN_KEY.ACCESS_TOKEN, encodeURIComponent(token), iframeCookieOptions);

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

export const getAuthTokenFromCookie = (): AuthToken => {
  const isLocal = Helper.getAppEnv() === APP_MODE.LOCAL;

  const accessToken = isLocal ? process.env.VUE_APP_ACCESS_TOKEN : Cookies.get(TOKEN_KEY.ACCESS_TOKEN);

  const idToken = isLocal ? process.env.VUE_APP_ID_TOKEN : Cookies.get(TOKEN_KEY.ID_TOKEN);

  return { accessToken: accessToken || null, idToken: idToken || null };
};

export const removeAuthToken = () => {
  Cookies.remove(TOKEN_KEY.ACCESS_TOKEN, cookieOptions);
  Cookies.remove(TOKEN_KEY.ID_TOKEN, cookieOptions);
};

export const isExpired = (jwtObject: Record<string, string>) => {
  const expiredDate = new Date(Number(jwtObject.exp) * 1000);
  const now = new Date();

  return now > expiredDate;
};

export const getClientId = (tenant: TenantType) =>
  tenant === TENANT.PARTNERS
    ? process.env.VUE_APP_MSAL_PARTNERS_CLIENT_ID || ''
    : process.env.VUE_APP_MSAL_HOMEOWNER_CLIENT_ID || '';

export const getTenantId = (tenant: TenantType) =>
  tenant === TENANT.PARTNERS
    ? process.env.VUE_APP_MSAL_PARTNERS_TENANT_ID || ''
    : process.env.VUE_APP_MSAL_HOMEOWNER_TENANT_ID || '';

export const getAuthority = (tenant: TenantType) =>
  tenant === TENANT.PARTNERS
    ? `https://${process.env.VUE_APP_MSAL_PARTNERS_TENANT_ID}.ciamlogin.com/${process.env.VUE_APP_MSAL_PARTNERS_TENANT_ID}`
    : `https://${process.env.VUE_APP_MSAL_HOMEOWNER_TENANT_ID}.ciamlogin.com/${process.env.VUE_APP_MSAL_HOMEOWNER_TENANT_ID}`;

/** MSAL 라이브러리 사용을 위한 Config 설정 */
export const getMSALConfig = (tenant: TenantType): Configuration => ({
  auth: {
    clientId: getClientId(tenant),
    authority: getAuthority(tenant),
    redirectUri: process.env.VUE_APP_MSAL_REDIRECT_URI || '',
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
});

export const getLoginRequestParams = (tenant: TenantType): RedirectRequest => ({
  scopes: [`${getClientId(tenant)}/.default`, 'openid', 'profile'],
});

export const getTenantByTenantId = (tenantId: string): TenantType =>
  tenantId === process.env.VUE_APP_MSAL_HOMEOWNER_TENANT_ID ? TENANT.HOMEOWNER : TENANT.PARTNERS;

export const getHomeownerConfig = () => getMSALConfig(TENANT.HOMEOWNER);

export const getPartnersConfig = () => getMSALConfig(TENANT.PARTNERS);

export const getMSALAccountInfo = (authToken?: AuthToken): AccountInfo | null => {
  const { accessToken } = authToken ?? getAuthTokenFromCookie();

  if (!accessToken) {
    return null;
  }

  const accessTokenJwt = parseJwt(accessToken);

  if (!accessTokenJwt) {
    return null;
  }

  const tenantId = getTenantByTenantId(accessTokenJwt.tid);

  const accountInfoKeySuffix = getTenantId(tenantId);

  const accountInfoKey = Object.keys(sessionStorage).find((key) => key.endsWith(accountInfoKeySuffix));

  if (!accountInfoKey) {
    return null;
  }

  const accountInfo = sessionStorage.getItem(accountInfoKey);

  return accountInfo ? JSON.parse(accountInfo) : null;
};

export const checkIsFleetAppProviderUrl = (url: string): boolean => {
  const fleetProviderList = [PROVIDER_URL.TEST, PROVIDER_URL.FLEET, PROVIDER_URL.FLEET_PROD];

  return fleetProviderList.some((provider) => url.includes(provider));
};

/**
 * iframe 등에서 document.referrer 가 비어도 Fleet 임베딩인지 판별.
 * Detect Fleet embed even when document.referrer is empty (e.g. iframe + Referrer-Policy).
 */
export const checkIsFleetEmbedContext = (): boolean => {
  if (checkIsFleetAppProviderUrl(document.referrer)) {
    return true;
  }

  const params = new URLSearchParams(window.location.search);
  if (
    params.get(FLEET_EMBED_PARAM.FLEET_EMBED) === FLEET_EMBED_PARAM_VALUE[FLEET_EMBED_PARAM.FLEET_EMBED] ||
    params.get(FLEET_EMBED_PARAM.FROM_FLEET) === FLEET_EMBED_PARAM_VALUE[FLEET_EMBED_PARAM.FROM_FLEET]
  ) {
    return true;
  }

  const ancestorOrigins = document.location.ancestorOrigins;
  if (!ancestorOrigins || ancestorOrigins.length === 0) {
    return false;
  }

  return Array.from(ancestorOrigins).some((origin) => !!origin && checkIsFleetAppProviderUrl(origin));
};

export const getAuthTypeCodeByFleetRole = (fleetRole: FleetRoleValue): RoleCodeValue => {
  return AUTH_TYPE_CODE_BY_FLEET_ROLE[fleetRole];
};
